/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from 'react';
import { DragLayer, DragLayerMonitor, XYCoord } from 'react-dnd';
import _ from 'lodash';

import { Context, IWithContextProps, useAppContext, withContext } from '@tock/coretypes';
import { DEFAULT_WALK_IN_NAME } from 'utils/service/walkInUtils';
import { DndItemTypes } from 'constants/dndItemTypes';
import { HD_CLIENT_THRESHOLD } from 'constants/UIConstants';
import { ServiceAerialTable } from 'components/service/aerial/ServiceAerialTable';
import { ServicePartyDragMode } from 'state/service/ui';
import { getClientWidth } from '@tock/shared/utils/responsive';
import {
  guestListShowNotesSelector,
  guestListShowTagsSelector,
  isHdModeEnabledSelector,
} from 'selectors/dashboardPreferenceSelectors';
import { servicePartyDragModeSelector } from 'selectors/service/serviceUiSelector';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import BookingDragLayer from 'components/timeline/BookingDragLayer';
import InventorySquare from 'components/table-based-templates/InventorySquare';
import NotifyListParty from 'components/service/NotifyList/NotifyListParty';
import Party from 'components/service/Party';
import PartyCard from 'components/service/party/PartyCard';
import PartyRedesignCardWithMemoryMitigation from 'components/service/party/partyRedesignCard/PartyRedesignCardWithMemoryMitigation';
import PartyRedesignNotifyCard from 'components/service/party/PartyRedesignNotifyCard';
import SettingsAerialTable from 'components/settings/tables/SettingsAerialTable';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

const isTouchDevice = 'ontouchstart' in window;

function getItemStyles(props: Props) {
  const { currentOffset, item } = props;
  if (!currentOffset) {
    return {
      display: 'none',
    };
  }

  let { x, y } = currentOffset;

  if (item.extraOffset) {
    x += item.extraOffset.x;
    y += item.extraOffset.y;
  }

  const transform = `translate(${x}px, ${y}px)`;
  return {
    display: 'inline-block',
    transform,
    WebkitTransform: transform,
  };
}

interface ConnectProps extends DefaultConnectProps {
  dragMode: ServicePartyDragMode;
  isHdMode: boolean;
  showGuestListNotes: boolean;
  showGuestListTags: boolean;
}

interface Props {
  currentOffset: XYCoord | null;
  isDragging: boolean;
  item: any;
  itemType: string | symbol | null;
}

/** For functional components. */
export function usePartyCardRedesign() {
  const {
    context: { isFlexBusiness },
  } = useAppContext();
  return useFeatureFlag(isFlexBusiness() ? 'PARTY_CARD_REDESIGN' : 'PARTY_CARD_REDESIGN_SLOT');
}

/** For class components with access to context, or in the utility functions below: */
export function isPartyCardRedesignEnabled({ isFeatureEnabled, isFlexBusiness }: Context) {
  return isFeatureEnabled(isFlexBusiness() ? 'PARTY_CARD_REDESIGN' : 'PARTY_CARD_REDESIGN_SLOT');
}

export function computePartyComponentRedesignVariant() {
  return PartyRedesignCardWithMemoryMitigation;
}

/**
 * a small utility for going from the feature status to the component to use for
 * PartyCards.
 */
export function computePartyComponent(context: Context) {
  return !context.isFeatureEnabled('TIMELINE_DRAG_TABLE_ASSIGNMENTS')
    ? Party
    : isPartyCardRedesignEnabled(context)
      ? computePartyComponentRedesignVariant()
      : PartyCard;
}

export function computeNotifyListPartyComponent(context: Context) {
  return isPartyCardRedesignEnabled(context) ? PartyRedesignNotifyCard : NotifyListParty;
}

class CustomDragLayer extends Component<Props & IWithContextProps & ConnectProps> {
  renderItem(type: string | symbol | null, item: any) {
    const {
      context,
      context: { isFeatureEnabled, isFlexBusiness: isFlexBusinessFunc },
      dragMode,
      showGuestListNotes,
      showGuestListTags,
      isHdMode,
    } = this.props;

    const isFlexBusiness = isFlexBusinessFunc();

    switch (type) {
      case DndItemTypes.PARTY: {
        const { party } = item;

        if (item.mode !== 'SERVICE') {
          return null;
        }

        if (isFeatureEnabled(isFlexBusiness ? 'PARTY_CARD_REDESIGN' : 'PARTY_CARD_REDESIGN_SLOT')) {
          let name = item.name ? item.name : (party.name ?? undefined);

          if (party?.type === 'WalkIn' && !name) {
            name = DEFAULT_WALK_IN_NAME;
          }

          return <BookingDragLayer name={name} />;
        }

        const PartyComponent = computePartyComponent(context);

        return (
          <div className="Party-dragPreview Party--singleList PartyList-sectionBody--windowed">
            <PartyComponent
              dragMode={dragMode}
              dragSource="PARTY"
              expandable={true}
              isSelected={false}
              isAssignable={true}
              isReadOnly={true}
              wasSelected={false}
              onSelectParty={_.noop}
              onSelectPartyForPreassign={_.noop}
              party={party}
              onTogglePartyPopover={_.noop}
              showGuestListNotes={showGuestListNotes}
              showGuestListTags={showGuestListTags}
              isHD={isHdMode && getClientWidth() > HD_CLIENT_THRESHOLD}
              isInTimeline={false}
            />
          </div>
        );
      }
      case DndItemTypes.SERVICE_AERIAL_TABLE: {
        return <ServiceAerialTable {...item} isDragPreview={true} />;
      }
      case DndItemTypes.SETTINGS_AERIAL_TABLE: {
        return <SettingsAerialTable {...item} isDragPreview={true} />;
      }
      case DndItemTypes.TEMPLATES_TEMPLATE_INFO: {
        const inventorySquareDetails = item.inventorySquareDetails;
        return (
          <InventorySquare
            colorClassName={inventorySquareDetails.colorClassName}
            displayColor={inventorySquareDetails.displayColor}
            text={inventorySquareDetails.text}
          />
        );
      }
      default: {
        if (item && item.renderedComponent && isTouchDevice) {
          return item.renderedComponent;
        } else {
          return null;
        }
      }
    }
  }

  render() {
    return (
      <div className="CustomDragLayer">
        {this.props.item && (
          <div style={getItemStyles(this.props)}>
            {this.renderItem(this.props.itemType, this.props.item)}
          </div>
        )}
      </div>
    );
  }
}

function collect(monitor: DragLayerMonitor) {
  return {
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
  };
}

export default connect<{}, ConnectProps>(
  DragLayer(collect)(withContext(CustomDragLayer)),
  (state: StoreType) => {
    return {
      dragMode: servicePartyDragModeSelector(state),
      isHdMode: isHdModeEnabledSelector(state),
      showGuestListNotes: guestListShowNotesSelector(state),
      showGuestListTags: guestListShowTagsSelector(state),
    };
  }
);
